import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box } from '@cbhq/cds-web/layout';
import { TextHeadline } from '@cbhq/cds-web/typography';
import { ActionType, ComponentType } from '@cbhq/client-analytics';

import { NIL_UUID } from '../../constants';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useClientProps } from '../../providers/ClientPropsProvider';
import { useProfileMenuV2 } from '../../providers/ProfileMenuV2Provider';

import { ItemContainer } from './ItemContainer';

const messages = defineMessages({
  addAccount: {
    defaultMessage: 'Add account',
    id: 'Add account',
    description: 'Label for add account in the user switcher menu.',
  },
  addAnotherProfile: {
    defaultMessage: 'Add another profile',
    id: 'AddAccountButton.addAnotherProfile',
    description: 'Accessibility label for the add another profile button.',
  },
});

export function AddAccountButton() {
  const { logEvent } = useAnalytics();
  const { config, onBeforeAddAccount } = useClientProps();
  const { labels } = useProfileMenuV2();
  const { formatMessage } = useIntl();

  const addAcctLabel = useMemo(
    () =>
      labels.find((label) => label.key === 'ADD_ACCOUNT')?.value ??
      formatMessage(messages.addAccount),
    [formatMessage, labels],
  );

  const handlePress = useCallback(async () => {
    try {
      await onBeforeAddAccount?.();
    } catch (error) {
      logEvent('user_switcher_add_account_callback_failed', {
        action: ActionType.click,
        componentType: ComponentType.button,
        actionType: 'user_switcher_add_account_button_clicked',
        error: error instanceof Error ? error.message : 'unknown error',
      });
    }

    logEvent('user_switcher_add_account', {
      action: ActionType.click,
      componentType: ComponentType.button,
      actionType: 'user_switcher_add_account_button_clicked',
    });

    const tid = crypto.randomUUID();
    const redirectParams = new URLSearchParams({
      authuser: NIL_UUID,
      action: 'add_account',
      tid,
      returnTo: window.location.href,
    });
    const redirect = `${config?.loginUrl ?? '/login'}?${redirectParams.toString()}`;

    // If it is inside of an iframe should postMessage instead of change location href
    if (window.self !== window.top) {
      window.parent.postMessage({ redirect }, '*');
    } else {
      window.location.href = redirect;
    }
  }, [onBeforeAddAccount, logEvent, config?.loginUrl]);

  return (
    <ItemContainer
      handleOnClick={handlePress}
      accessibilityLabel={formatMessage(messages.addAnotherProfile)}
    >
      <Box width="32px" height="32px" justifyContent="center" alignItems="center">
        <Icon size="m" name="followAdd" color="fg" />
      </Box>
      <TextHeadline as="p" color="fg" display="block">
        {addAcctLabel}
      </TextHeadline>
    </ItemContainer>
  );
}
